import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserLock, faCopy, faCheck, faKey, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import './RepairCodePanel.css';

const formatExpiry = (iso) =>
  new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

// "Mot de passe Desktop": a Lonnii Desktop shop owner who has forgotten their password and has no internet (so no
// emailed code) reads out the account id from their "Mot de passe oublié" screen. The code issued here lets them set
// a new password once, for that account on that server. It hands over the shop, so check who is calling first.
const ResetCodePanel = () => {
  const [account, setAccount] = useState('');
  const [hours, setHours] = useState('24');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    setCopied(false);

    try {
      const response = await api.post('/dashboard-admin/reset-code', { account, hours: Number(hours), note });
      setResult(response.data);
    } catch (err) {
      const status = err.response?.status;
      setError(
        err.response?.data?.error ||
          (status === 404
            ? 'Le serveur ne connaît pas encore cette fonction (404). Le backend doit être déployé et rechargé.'
            : status
              ? `Erreur du serveur (${status})`
              : 'Serveur injoignable')
      );
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // The code stays selectable on screen if the clipboard is refused.
    }
  };

  return (
    <div className="settings-section repair-panel">
      <h3>
        <FontAwesomeIcon icon={faUserLock} /> Mot de passe Desktop
      </h3>
      <p className="repair-intro">
        Pour l'<strong>administrateur général</strong> d'un magasin Lonnii Desktop qui a oublié son mot de passe et
        n'a pas Internet. Il lit l'<strong>identifiant du compte</strong> affiché sur son écran « Mot de passe
        oublié » (lien « Pas d'Internet ? »). Le code ne vaut que pour ce compte, sur ce serveur, une seule fois.
      </p>
      <p className="repair-warning">
        <FontAwesomeIcon icon={faExclamationTriangle} /> Ce code donne accès à tout le magasin. Vérifiez l'identité
        de l'appelant avant de le générer.
      </p>

      <form onSubmit={generate} className="repair-form">
        <div className="repair-field">
          <label htmlFor="reset-account">Identifiant du compte</label>
          <input
            id="reset-account"
            type="text"
            placeholder="A1B2-C3D4-E5F6-0718"
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            required
          />
        </div>

        <div className="repair-field">
          <label htmlFor="reset-note">Note (magasin, appelant, vérification faite)</label>
          <input
            id="reset-note"
            type="text"
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            autoComplete="off"
          />
          <small className="repair-hint">Consignée dans le journal d'audit avec la demande.</small>
        </div>

        <div className="repair-field repair-field-short">
          <label htmlFor="reset-hours">Valable (heures)</label>
          <input
            id="reset-hours"
            type="number"
            min="1"
            max="168"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            required
          />
        </div>

        {error && <div className="repair-error">{error}</div>}

        <button type="submit" className="btn-primary repair-submit" disabled={busy}>
          <FontAwesomeIcon icon={faKey} /> {busy ? 'Génération…' : 'Générer le code'}
        </button>
      </form>

      {result && (
        <div className="repair-result">
          <div className="repair-result-head">
            <strong>Code de réinitialisation</strong>
            <span>
              Compte {result.account} · valable jusqu'au {formatExpiry(result.expiresAt)} · une seule fois
            </span>
          </div>
          <code className="repair-code">{result.code}</code>
          <button type="button" className="btn-secondary" onClick={copy}>
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} /> {copied ? 'Copié' : 'Copier'}
          </button>
          <p className="repair-note">
            Transmettez-le au client par téléphone ou message. Il est consigné dans le journal d'audit (sans le code
            lui-même).
          </p>
        </div>
      )}
    </div>
  );
};

export default ResetCodePanel;
